/**
 * 充值管理（管理面板 Tab）
 * 1) 累计收入 + 近 12 个月收入曲线图（轻量 SVG，无图表库依赖）
 * 2) 充值套餐管理（金额↔积分，增/改/删，来自 recharge_plan 表）
 * 3) 用户充值记录（用户/金额/积分/方式/当前剩余积分/时间；点击用户看累计充值）
 */
import React, { useCallback, useEffect, useState } from 'react';
import { payAdminApi, getErrorMessage } from '../services/api';

interface Plan { id: number; key: string; name: string; price: number; points: number; is_active: boolean; sort: number; }
interface Rec { order_no: string; user_id: number; username: string; amount: number; points: number; payment_method: string; user_points: number; paid_at: string | null; }

function MonthChart({ data }: { data: Array<{ month: string; amount: number }> }) {
  const months: string[] = [];
  const now = new Date();
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  const map = new Map(data.map((d) => [d.month, d.amount]));
  const values = months.map((m) => map.get(m) ?? 0);
  const max = Math.max(...values, 1);
  const W = 640, H = 180, P = 34;
  const pts = values.map((v, i) => {
    const x = P + (i * (W - 2 * P)) / (values.length - 1);
    const y = H - P - (v / max) * (H - 2 * P);
    return [x, y] as const;
  });
  const path = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-44">
      {[0, 0.5, 1].map((r) => {
        const y = H - P - r * (H - 2 * P);
        return <line key={r} x1={P} x2={W - P} y1={y} y2={y} stroke="#e5e7eb" />;
      })}
      <path d={path} fill="none" stroke="#2563eb" strokeWidth="2" />
      {pts.map((p, i) => (
        <circle key={i} cx={p[0]} cy={p[1]} r={values[i] > 0 ? 3.5 : 2} fill={values[i] > 0 ? '#2563eb' : '#cbd5e1'}>
          <title>{`${months[i]}: ¥${values[i]}`}</title>
        </circle>
      ))}
      {months.map((m, i) => i % 2 === 0 && (
        <text key={m} x={pts[i][0]} y={H - 10} fontSize="10" fill="#6b7280" textAnchor="middle">{m.slice(2).replace('-', '/')}</text>
      ))}
      <text x={4} y={P + 2} fontSize="10" fill="#9ca3af">¥{max}</text>
    </svg>
  );
}

export default function RechargeMgmtTab() {
  const [stats, setStats] = useState<{ total_revenue: number; paid_count: number; monthly: Array<{ month: string; amount: number }> } | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [records, setRecords] = useState<Rec[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [editing, setEditing] = useState<Plan | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ key: '', name: '', price: '', points: '', is_active: true, sort: '0' });
  const [userTotal, setUserTotal] = useState<{ username: string; total_amount: number; total_points: number; count: number } | null>(null);

  const loadStats = useCallback(() => { payAdminApi.stats().then((r) => setStats(r.data)).catch(() => {}); }, []);
  const loadPlans = useCallback(() => { payAdminApi.plans().then((r) => setPlans(r.data.plans)).catch(() => {}); }, []);
  const loadRecords = useCallback((p: number) => {
    payAdminApi.records(p, pageSize).then((r) => { setRecords(r.data.records); setTotal(r.data.total); }).catch(() => {});
  }, []);

  useEffect(() => { loadStats(); loadPlans(); }, [loadStats, loadPlans]);
  useEffect(() => { loadRecords(page); }, [page, loadRecords]);

  const openCreate = () => { setEditing(null); setForm({ key: '', name: '', price: '', points: '', is_active: true, sort: '0' }); setShowForm(true); };
  const openEdit = (p: Plan) => { setEditing(p); setForm({ key: p.key, name: p.name, price: String(p.price), points: String(p.points), is_active: p.is_active, sort: String(p.sort) }); setShowForm(true); };

  const savePlan = async () => {
    const data = { key: form.key.trim(), name: form.name.trim(), price: parseFloat(form.price), points: parseInt(form.points, 10), is_active: form.is_active, sort: parseInt(form.sort, 10) || 0 };
    if (!data.key || !data.name || !(data.price > 0) || !(data.points > 0)) { setMsg({ type: 'error', text: '请完整填写 key/名称/金额/积分' }); return; }
    try {
      if (editing) { await payAdminApi.updatePlan(editing.id, data); setMsg({ type: 'success', text: '已更新' }); }
      else { await payAdminApi.createPlan(data); setMsg({ type: 'success', text: '已新增' }); }
      setShowForm(false); loadPlans();
    } catch (e) { setMsg({ type: 'error', text: getErrorMessage(e, '保存失败') }); }
  };

  const deletePlan = async (p: Plan) => {
    if (!window.confirm(`删除套餐「${p.name}」？`)) return;
    try { await payAdminApi.deletePlan(p.id); setMsg({ type: 'success', text: '已删除' }); loadPlans(); }
    catch (e) { setMsg({ type: 'error', text: getErrorMessage(e, '删除失败') }); }
  };

  const showUserTotal = async (r: Rec) => {
    try { const res = await payAdminApi.userTotal(r.user_id); setUserTotal({ username: r.username, ...res.data }); }
    catch (e) { setMsg({ type: 'error', text: getErrorMessage(e, '查询失败') }); }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6">
      {/* 收入概览 + 月曲线 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
          <div className="text-sm text-green-600">累计收入</div>
          <div className="text-3xl font-bold text-green-700 mt-1">¥{(stats?.total_revenue ?? 0).toLocaleString()}</div>
          <div className="text-xs text-green-600 mt-1">已支付 {stats?.paid_count ?? 0} 笔</div>
        </div>
        <div className="lg:col-span-2 border border-gray-200 rounded-lg p-4">
          <div className="text-sm font-medium text-gray-700 mb-1">近 12 个月收入</div>
          <MonthChart data={stats?.monthly ?? []} />
        </div>
      </div>

      {/* 套餐管理 */}
      <div className="border border-gray-200 rounded-lg">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <span className="text-sm font-medium text-gray-700">充值套餐（金额 ↔ 积分）</span>
          <button onClick={openCreate} className="px-3 py-1.5 text-sm rounded-lg text-white bg-blue-600 hover:bg-blue-700">新增套餐</button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-gray-500">
              <tr className="border-b">
                <th className="text-left px-4 py-2">名称</th>
                <th className="text-left px-4 py-2">金额(元)</th>
                <th className="text-left px-4 py-2">积分</th>
                <th className="text-left px-4 py-2">状态</th>
                <th className="text-left px-4 py-2">排序</th>
                <th className="text-right px-4 py-2">操作</th>
              </tr>
            </thead>
            <tbody>
              {plans.map((p) => (
                <tr key={p.id} className="border-b last:border-0">
                  <td className="px-4 py-2">{p.name}<span className="text-gray-400 text-xs ml-1">({p.key})</span></td>
                  <td className="px-4 py-2">¥{p.price}</td>
                  <td className="px-4 py-2">{p.points.toLocaleString()}</td>
                  <td className="px-4 py-2">{p.is_active ? <span className="text-green-600">上架</span> : <span className="text-gray-400">下架</span>}</td>
                  <td className="px-4 py-2">{p.sort}</td>
                  <td className="px-4 py-2 text-right space-x-2">
                    <button onClick={() => openEdit(p)} className="text-blue-600 hover:underline">编辑</button>
                    <button onClick={() => deletePlan(p)} className="text-red-600 hover:underline">删除</button>
                  </td>
                </tr>
              ))}
              {plans.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">暂无套餐</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* 充值记录 */}
      <div className="border border-gray-200 rounded-lg">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 text-sm font-medium text-gray-700">充值记录（{total}）</div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-gray-500">
              <tr className="border-b">
                <th className="text-left px-4 py-2">用户</th>
                <th className="text-left px-4 py-2">充值金额</th>
                <th className="text-left px-4 py-2">充值积分</th>
                <th className="text-left px-4 py-2">方式</th>
                <th className="text-left px-4 py-2">当前剩余积分</th>
                <th className="text-left px-4 py-2">充值时间</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => (
                <tr key={r.order_no} className="border-b last:border-0">
                  <td className="px-4 py-2"><button onClick={() => showUserTotal(r)} className="text-blue-600 hover:underline" title="点击查看累计充值">{r.username}</button></td>
                  <td className="px-4 py-2">¥{r.amount.toFixed(2)}</td>
                  <td className="px-4 py-2">{r.points.toLocaleString()}</td>
                  <td className="px-4 py-2">{r.payment_method === 'alipay' ? '支付宝' : '微信'}</td>
                  <td className="px-4 py-2">{r.user_points.toLocaleString()}</td>
                  <td className="px-4 py-2 text-gray-500">{r.paid_at ? r.paid_at.replace('T', ' ').slice(0, 19) : '-'}</td>
                </tr>
              ))}
              {records.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">暂无充值记录</td></tr>}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-end gap-2 px-4 py-3 text-sm">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-2 py-1 border rounded disabled:opacity-40">上一页</button>
            <span className="text-gray-500">{page} / {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="px-2 py-1 border rounded disabled:opacity-40">下一页</button>
          </div>
        )}
      </div>

      {msg && <div className={`px-4 py-2.5 rounded-lg text-sm ${msg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>{msg.text}</div>}

      {/* 套餐表单弹窗 */}
      {showForm && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowForm(false)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-5 space-y-3">
            <div className="text-base font-semibold text-gray-800">{editing ? '编辑套餐' : '新增套餐'}</div>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm">key<input value={form.key} disabled={!!editing} onChange={(e) => setForm({ ...form, key: e.target.value })} className="w-full mt-1 px-2 py-1.5 border rounded disabled:bg-gray-100" placeholder="plan_x" /></label>
              <label className="text-sm">名称<input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full mt-1 px-2 py-1.5 border rounded" placeholder="19元/20万积分" /></label>
              <label className="text-sm">金额(元)<input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="w-full mt-1 px-2 py-1.5 border rounded" /></label>
              <label className="text-sm">积分<input type="number" value={form.points} onChange={(e) => setForm({ ...form, points: e.target.value })} className="w-full mt-1 px-2 py-1.5 border rounded" /></label>
              <label className="text-sm">排序<input type="number" value={form.sort} onChange={(e) => setForm({ ...form, sort: e.target.value })} className="w-full mt-1 px-2 py-1.5 border rounded" /></label>
              <label className="text-sm flex items-center gap-2 mt-6"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />上架</label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowForm(false)} className="px-3 py-1.5 text-sm rounded-lg border border-gray-300">取消</button>
              <button onClick={savePlan} className="px-3 py-1.5 text-sm rounded-lg text-white bg-blue-600 hover:bg-blue-700">保存</button>
            </div>
          </div>
        </div>
      )}

      {/* 用户累计充值弹窗 */}
      {userTotal && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setUserTotal(null)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-5 space-y-2">
            <div className="text-base font-semibold text-gray-800">{userTotal.username} · 累计充值</div>
            <div className="text-sm text-gray-600">累计金额：<b className="text-green-700">¥{userTotal.total_amount.toLocaleString()}</b></div>
            <div className="text-sm text-gray-600">累计积分：<b>{userTotal.total_points.toLocaleString()}</b></div>
            <div className="text-sm text-gray-600">充值笔数：<b>{userTotal.count}</b></div>
            <div className="flex justify-end pt-2"><button onClick={() => setUserTotal(null)} className="px-3 py-1.5 text-sm rounded-lg border border-gray-300">关闭</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
